import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { BhcHeader } from '@/components/health-check/BhcHeader';
import { BhcFooter } from '@/components/health-check/BhcFooter';
import '@/styles/business-health-check.css';
import { useJsonLd } from '@/hooks/useJsonLd';

const QUALIFY = '/business-health-check/qualifier?v=trust';
const BUSINESS = '/business-health-check';

const IMG_TRUST = '/business-health-check/trust-estate.jpg'; // reviewing/signing estate & trust documents

const Ph = ({ tag, ratio = '4/5', style, src, alt }) => (
  <div className="ph" style={{ aspectRatio: ratio, ...style }}>
    {src
      ? <img src={src} alt={alt || tag} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      : <span className="ph__tag">{tag}</span>}
  </div>
);

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org', '@type': 'Service',
  name: 'Trust & Estate Health Check', serviceType: 'Fixed-fee fiduciary accounting and tax diagnostic',
  description: 'A fixed-fee CPA diagnostic for trustees and executors: governing-document read-through, fiduciary accounting review, fiduciary income-tax projection (Form 1041), estimated-tax adequacy check, and trustee/executor compliance review, with a written findings report and a 60-minute strategy meeting.',
  provider: { '@type': 'AccountingService', name: 'Kohari Gonzalez Oneyear & Brown PLLC', telephone: '+1-704-599-3355' },
  areaServed: 'US', offers: { '@type': 'Offer', price: '4995.00', priceCurrency: 'USD' },
};

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://kgob.com/' },
    { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://kgob.com/services' },
    { '@type': 'ListItem', position: 3, name: 'Trust & Estate Health Check', item: 'https://kgob.com/business-health-check/trust-estate' },
  ],
};

const INCLUDED = [
  ['01', 'Governing-document read-through', "Trust instrument or will reviewed to identify entity type (simple / complex / grantor / decedent's estate), income vs. remainder beneficiaries, and distribution standards."],
  ['02', 'Fiduciary (trust) accounting review', "Principal-and-income allocation under NC's Principal and Income Act (G.S. Ch. 37A); checks that principal and income ledgers reconcile and flags common misallocations."],
  ['03', 'Mid-year fiduciary income-tax projection', 'Models Distributable Net Income (DNI), the income distribution deduction, and the compressed trust brackets — including distribution scenarios and the 65-day rule.'],
  ['04', 'Estimated-tax adequacy check', 'Review of the Form 1041-ES position for federal purposes. (Note: North Carolina does not require estimated tax of a fiduciary.)'],
  ['05', 'Trustee / executor compliance & liability review', 'Filing status and deadlines (federal Form 1041; NC Form D-407), K-1 timeliness, and red flags that can create personal liability.'],
  ['06', 'Written findings report + 60-min strategy', 'Everything we found in plain English, with a prioritized action plan, and a working session to walk through it.'],
];

const FAQS = [
  ["What's the difference between trust accounting and trust tax?", "They're not the same. Trust accounting allocates receipts and disbursements between income and principal under state law — it determines what an income beneficiary receives. Trust tax determines what's taxable. Fiduciary accounting income is not the same number as taxable income, and confusing the two is a common, costly mistake.", true],
  ['Do you prepare the 1041?', "Not in this engagement — it's a diagnostic. If the review shows a return is needed or worth revisiting, we'll quote that separately with a fixed price.", false],
  ['Can you change the trust or give legal advice?', "No. We identify issues and work with your attorney; we don't draft, amend, or interpret legal documents. Where a legal question surfaces, we'll point it out and recommend you consult counsel.", false],
  ["I'm a beneficiary, not the trustee — can I order this?", "In many cases yes, with appropriate authorization and limits. We'll confirm what's possible based on your role and the situation when you qualify.", false],
  ['Is the fee refundable?', 'No — like the business version, this is a fixed-fee engagement and work begins promptly after you sign the terms and submit records. The fee is non-refundable once work commences, which is exactly what you agree to before paying.', false],
];

export default function TrustEstateHealthCheck() {
    useJsonLd('trust-estate-0', SERVICE_SCHEMA);
  useJsonLd('trust-estate-1', BREADCRUMB_SCHEMA);

  return (
    <div className="bhc-root bhc-trust">
      <Helmet>
        <title>Trust & Estate Health Check | Fiduciary CPA Review | KGOB</title>
        <meta name="description" content="An independent, fixed-fee fiduciary diagnostic: accounting review, fiduciary tax projection, and trustee compliance review. We coordinate with your attorney." />
        <link rel="canonical" href="https://kgob.com/business-health-check/trust-estate" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,300;6..72,400;6..72,500&family=Newsreader:ital,opsz,wght@1,6..72,400&family=Figtree:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Trust & Estate Health Check | Fiduciary CPA Review | KGOB" />
        <meta property="og:description" content="An independent, fixed-fee fiduciary diagnostic: accounting review, fiduciary tax projection, and trustee compliance review. We coordinate with your attorney." />
        <meta property="og:url" content="https://kgob.com/business-health-check/trust-estate" />
      </Helmet>

      <BhcHeader
        nav={[
          { label: "What's included", href: '#what' },
          { label: "Who it's for", href: '#who' },
          { label: 'FAQ', href: '#faq' },
          { label: 'Business version', to: BUSINESS },
        ]}
        cta={{ label: 'See If You Qualify', to: QUALIFY }}
      />

      <main>
        {/* HERO */}
        <section className="heroT wrap" data-testid="hero">
          <div className="heroT__grid">
            <div>
              <span className="eyebrow">Trust &amp; Estate Health Check · Fixed Fee</span>
              <h1 className="display-1" style={{ marginTop: '22px' }}>Are you sure the trust or estate is being <span className="em-italic">handled right?</span></h1>
              <p className="lede" style={{ marginTop: '22px', maxWidth: '48ch' }}>Serving as a trustee or executor comes with real personal responsibility — and real personal liability. We'll review the accounting, estimate the fiduciary tax exposure, and flag compliance gaps before they become problems.</p>
              <ul className="fearstrip">
                <li>"Am I meeting my duties?"</li>
                <li>"Is the fiduciary tax handled correctly?"</li>
                <li>"Could I be personally on the hook?"</li>
              </ul>
              <div className="offerline">
                <span className="offerline__price">$4,995</span>
                <span className="offerline__label">One fixed fee. An independent read<br />on how it's being administered and taxed.</span>
              </div>
              <div className="heroT__cta">
                <Link className="btn btn--primary btn--lg" to={QUALIFY} data-testid="hero-cta">See If You Qualify <span className="arrow">→</span></Link>
              </div>
              <div className="trust-strip">
                <span>Licensed NC CPAs</span><span className="dot" />
                <span>We coordinate with your attorney</span><span className="dot" />
                <span>Charlotte · Mooresville · Winston-Salem</span>
              </div>
            </div>
            <div className="heroT__media">
              <Ph tag="Trustee / advisor photo" ratio="4/5" src={IMG_TRUST} alt="Reviewing trust and estate documents with a KGOB advisor" style={{ boxShadow: 'var(--shadow-lg)' }} />
            </div>
          </div>
        </section>

        {/* HOOK */}
        <section className="hook section" data-testid="hook">
          <div className="wrap">
            <span className="eyebrow eyebrow--on-dark eyebrow--center">Why mid-year matters</span>
            <div className="hook__big" style={{ marginTop: '18px' }}>~$16,000</div>
            <p>Trusts reach the <strong style={{ color: '#fff' }}>top federal tax bracket</strong> at only about $16,000 of taxable income — dramatically faster than individuals. That's exactly why fiduciary tax planning mid-year, while distribution decisions and the 65-day rule are still on the table, can matter so much.</p>
            <p className="muted" style={{ color: 'rgba(255,255,255,.5)', fontSize: '13px', marginTop: '16px' }}>Educational, not a guarantee of savings. Your situation depends on the governing document, income, and distributions.</p>
          </div>
        </section>

        {/* WHO */}
        <section className="section" id="who" data-testid="who">
          <div className="wrap">
            <div className="center" style={{ marginBottom: '40px' }}>
              <span className="eyebrow eyebrow--center">Who It's For</span>
              <h2 className="display-2" style={{ marginTop: '16px' }}>Built for fiduciaries who want certainty.</h2>
              <p className="lede" style={{ marginTop: '18px', maxWidth: '60ch', marginInline: 'auto' }}>Trustees, executors, personal representatives, and beneficiaries who want an independent read on how a trust or estate is being administered and taxed — before a filing deadline or a beneficiary question forces the issue.</p>
            </div>
          </div>
        </section>

        {/* INCLUDED */}
        <section className="section" id="what" style={{ background: 'var(--card)', borderBlock: '1px solid var(--line)' }} data-testid="included">
          <div className="wrap">
            <div className="center" style={{ marginBottom: '42px' }}>
              <span className="eyebrow eyebrow--center">What's Included</span>
              <h2 className="display-2" style={{ marginTop: '16px' }}>One fixed-fee fiduciary diagnostic.</h2>
            </div>
            <div className="stack-grid">
              {INCLUDED.map(([no, h, p]) => (
                <div className="stack-item" key={no}><span className="stack-item__no">{no}</span><h3>{h}</h3><p>{p}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* NOT INCLUDED */}
        <section className="section" data-testid="not-included">
          <div className="wrap te-split" style={{ gridTemplateColumns: '1fr 1.1fr', gap: 'clamp(32px,5vw,64px)' }}>
            <div>
              <span className="eyebrow">What's Not Included</span>
              <h2 className="display-2" style={{ marginTop: '18px' }}>A diagnostic — quoted separately for the rest.</h2>
              <p className="lede" style={{ marginTop: '20px' }}>These are excluded and, if you want our help, <strong>quoted separately in writing</strong> with a fixed price.</p>
            </div>
            <div>
              <ul className="tags">
                <li>Preparation of Form 1041 / NC D-407</li>
                <li>Form 706 (estate tax) or 709 (gift tax)</li>
                <li>Formal court / judicial accountings</li>
                <li>Acting as trustee / trust administration</li>
                <li>Litigation or beneficiary-dispute support</li>
                <li>Drafting or interpreting legal documents</li>
              </ul>
              <div className="upl"><strong>A clear line we don't cross:</strong> We identify issues and coordinate with your attorney — we do <strong>not</strong> provide legal advice or draft, amend, or interpret the legal effect of trust or estate documents. A CPA may flag an issue and recommend you consult counsel.</div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" id="faq" style={{ background: 'var(--paper-2)' }} data-testid="faq">
          <div className="wrap te-split" style={{ gridTemplateColumns: '.65fr 1.35fr', gap: 'clamp(28px,4vw,56px)' }}>
            <div>
              <span className="eyebrow">FAQ</span>
              <h2 className="display-2" style={{ marginTop: '18px' }}>Fiduciary questions, answered.</h2>
              <p className="muted" style={{ marginTop: '18px', maxWidth: '30ch' }}>Looking for the operating-business version? <Link data-testid="trust-estate-link-1" to={BUSINESS} style={{ color: 'var(--navy-800)', borderBottom: '1px solid var(--gold-500)' }}>See the Business Health Check →</Link></p>
            </div>
            <div>
              {FAQS.map(([q, a, open]) => (
                <details data-testid="trust-estate-details-1" className="faq-item" key={q} open={open}>
                  <summary data-testid="trust-estate-summary-1">{q}<span className="pm">+</span></summary>
                  <div className="faq-item__body">{a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="finalcta section" data-testid="final-cta">
          <div className="wrap finalcta__inner">
            <span className="eyebrow eyebrow--center eyebrow--on-dark">For Trustees &amp; Executors</span>
            <h2 className="display-1" style={{ marginTop: '18px' }}>Know the trust is being handled right.</h2>
            <p>An independent, fixed-fee read on the accounting, the fiduciary tax exposure, and the compliance gaps that create personal liability.</p>
            <div style={{ marginTop: '30px' }}><Link className="btn btn--primary btn--lg" to={QUALIFY} data-testid="final-cta-btn">See If You Qualify <span className="arrow">→</span></Link></div>
          </div>
        </section>
      </main>

      <BhcFooter
        engagementLinks={[
          { label: "What's included", href: '#what' },
          { label: "Who it's for", href: '#who' },
          { label: 'See if you qualify', to: QUALIFY },
          { label: 'Business version', to: BUSINESS },
        ]}
        disclaimer="This page describes a fixed-fee professional service offering. It is not tax, legal, or financial advice, and does not constitute the practice of law. Tax projections are estimates based on information provided and are not a guarantee of any result. The binding terms of any engagement are set out in the Engagement Terms & Conditions you accept before payment."
      />
    </div>
  );
}
